/**
 * Main App Component with React Router 7
 */

import { Routes, Route, Navigate } from "react-router"
import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { NavigationProvider, SessionProvider } from "@audra/marketing-ui"
import { rrAdapter } from "@/lib/marketing-nav-adapter"
import { AuthProvider } from "@/hooks/use-auth"
import { Toaster } from "@/components/ui/toaster"
import { TooltipProvider } from "@/components/ui/tooltip"
import { AuthGuard } from "@/components/auth/auth-guard"
import { DashboardLayout } from "@/components/layout/dashboard-layout"
import { AdminLayout } from "@/components/layout/admin-layout"

// Page components
import LoginPage from "@/routes/login"
import DashboardPage from "@/routes/dashboard"
import ProjectPage from "@/routes/projects/[id]"
import ProjectsPage from "@/routes/projects"
import SettingsPage from "@/routes/settings"
import TemplatesPage from "@/routes/templates"
import HelpPage from "@/routes/help"
import AdminUsersPage from "@/routes/admin/users"
import AdminTemplatesPage from "@/routes/admin/templates"
import AdminTemplatePage from "@/routes/admin/templates/[id]"
import AdminPromptsPage from "@/routes/admin/prompts"
import AdminPromptPage from "@/routes/admin/prompts/[id]"
import NotFoundPage from "@/routes/errors/not-found"
import ServerErrorPage from "@/routes/errors/server-error"
import PrivacyPage from "@/routes/privacy"
import TermsPage from "@/routes/terms"
import { ErrorBoundary } from "@/components/errors/error-boundary"

// audra-id consumer integration. The shared <SessionProvider> hydrates
// from id.audra.digital/api/me when the audra_session cookie is present;
// the <AppSwitcher> in the chrome reads from that context. Off by default
// — local JWT + localStorage auth keeps working unchanged when the flag
// is off.
const AUDRA_ID_ENABLED = import.meta.env.VITE_AUDRA_ID_ENABLED === "true"
const AUDRA_ID_ORIGIN =
  import.meta.env.VITE_AUDRA_ID_ORIGIN || "https://staging.id.windmill.ai"

// Create query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000, // 1 minute
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
})

// Layout wrapper for dashboard pages
function DashboardLayoutWrapper({ children }: { children: React.ReactNode }) {
  return <DashboardLayout>{children}</DashboardLayout>
}

export function App() {
  // The legacy AuthProvider stays in charge of routing/AuthGuard; the
  // audra-id SessionProvider only feeds the chrome (AppSwitcher) when
  // enabled. `on401="return-null"` keeps it quiet when no audra_session
  // cookie is present — no logout, no redirect.
  const tree = (
    <NavigationProvider adapter={rrAdapter}>
      <AuthProvider>
        <TooltipProvider>
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/terms" element={<TermsPage />} />

            {/* Protected routes with DashboardLayout */}
            <Route element={<AuthGuard />}>
              <Route path="/dashboard" element={
                <DashboardLayoutWrapper>
                  <DashboardPage />
                </DashboardLayoutWrapper>
              } />
              <Route path="/projects" element={
                <DashboardLayoutWrapper>
                  <ProjectsPage />
                </DashboardLayoutWrapper>
              } />
              <Route path="/settings" element={
                <DashboardLayoutWrapper>
                  <SettingsPage />
                </DashboardLayoutWrapper>
              } />
              <Route path="/templates" element={
                <DashboardLayoutWrapper>
                  <TemplatesPage />
                </DashboardLayoutWrapper>
              } />
              <Route path="/help" element={
                <DashboardLayoutWrapper>
                  <HelpPage />
                </DashboardLayoutWrapper>
              } />

              {/* Admin routes with AdminLayout (sidebar, no header) */}
              <Route path="/admin/users" element={
                <AdminLayout>
                  <AdminUsersPage />
                </AdminLayout>
              } />
              <Route path="/admin/templates" element={
                <AdminLayout>
                  <AdminTemplatesPage />
                </AdminLayout>
              } />
              {/* Template detail page has its own full-screen layout (no sidebar) */}
              <Route path="/admin/templates/:id" element={<AdminTemplatePage />} />
              <Route path="/admin/prompts" element={
                <AdminLayout>
                  <AdminPromptsPage />
                </AdminLayout>
              } />
              {/* Prompt detail page has its own full-screen layout (no sidebar) */}
              <Route path="/admin/prompts/:id" element={<AdminPromptPage />} />

              {/* Project page has its own layout (no wrapper) */}
              <Route path="/projects/:id" element={<ProjectPage />} />
            </Route>

            {/* Error pages */}
            <Route path="/500" element={<ServerErrorPage />} />

            {/* Redirects */}
            <Route path="/" element={<Navigate to="/login" replace />} />

            {/* 404 - Catch all unmatched routes */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
          <Toaster />
        </TooltipProvider>
      </AuthProvider>
    </NavigationProvider>
  )

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        {AUDRA_ID_ENABLED ? (
          <SessionProvider audraIdOrigin={AUDRA_ID_ORIGIN} on401="return-null">
            {tree}
          </SessionProvider>
        ) : (
          tree
        )}
      </QueryClientProvider>
    </ErrorBoundary>
  )
}
